import { useRouter } from 'vue-router';
import type { PersonaId } from '@/demo/monde';
import { useAuthStore } from '@/stores/auth';
import { useDemandesStore } from '@/stores/demandes';
import { useDecouverteStore } from '@/stores/decouverte';
import { couperTempsReel } from '@/composables/useTempsReel';

/**
 * ENTRER, CHANGER DE RÔLE, SORTIR : chaque bascule change de MONDE (vraies
 * données ↔ données fictives, ou un rôle pour un autre). Il faut alors, dans
 * cet ordre : basculer l'interrupteur, oublier ce qui a été chargé de l'autre
 * monde (référentiels, liste, fiche), couper le temps réel, relire l'identité
 * — le menu et les gardes de route en dépendent —, puis remonter l'écran.
 *
 * Centralisé ici pour que le bandeau, la page de connexion, le menu profil et
 * la visite guidée basculent exactement de la même façon (repris de l'outil IGD).
 */
export function useModeDemo() {
  const auth = useAuthStore();
  const demandes = useDemandesStore();
  const decouverte = useDecouverteStore();
  const router = useRouter();

  function oublier(): void {
    demandes.$reset();
    couperTempsReel();
  }

  /** Après une bascule : l'écran courant est-il encore permis au nouveau rôle ? */
  async function rester(): Promise<void> {
    const courant = router.currentRoute.value;
    if (courant.meta.peut && !courant.meta.peut(auth.roles, auth.user?.kind ?? null)) await router.replace('/');
  }

  async function entrer(persona?: PersonaId, destination = '/decouverte'): Promise<void> {
    // Les rôles de la personne RÉELLE, avant que l'identité devienne celle du personnage.
    await decouverte.entrer(persona, auth.user ? [...auth.roles] : null, auth.user?.kind ?? null);
    oublier();
    await auth.fetchMe();
    await router.push(destination);
  }

  async function changerPersona(persona: PersonaId): Promise<void> {
    if (decouverte.persona === persona && auth.isAuthenticated) return;
    await decouverte.changerPersona(persona);
    oublier();
    await auth.fetchMe();
    await rester();
  }

  async function reinitialiser(): Promise<void> {
    await decouverte.reinitialiser();
    oublier();
    await auth.fetchMe();
    await rester();
  }

  async function quitter(): Promise<void> {
    decouverte.quitter();
    oublier();
    // Relit l'identité RÉELLE (cookie de session) : sans session, retour à la connexion.
    await auth.init();
    await router.replace(auth.isAuthenticated ? '/' : '/login');
  }

  return { entrer, changerPersona, reinitialiser, quitter };
}
